import {
  getErrorCodeFromUnknown,
  getErrorTypeFromUnknown,
  getStringAtPath,
  isRecord,
  type UserMessage,
} from "@atoms/atom-types";
import {
  GraphQLRequestError,
  getSentryEventIdFromUnknown,
  getWireUserMessage,
} from "../graphql/GraphQLRequestError";

/**
 * What the client knows about a failed request, read the same way for every
 * transport: generated one-shot calls, subscriptions, live packets, non-live
 * finds and anything else that was thrown.
 *
 * The shape may grow; treat unknown fields as optional. `errorClass` and
 * `errorRef` are passed through as the backend sent them and are absent
 * against a backend that does not send them.
 */
export type NormalizedError = Readonly<{
  /** Stable error code, e.g. `permission/insufficient` or a project code. */
  code?: string;
  /** Top-level error type, where the transport carries it (live packets). */
  type?: string;
  errorClass?: string;
  errorRef?: string;
  /** Id of the event the backend reported for this failure. */
  sentryEventId?: string;
  /** Name of the failed operation, e.g. `updateProject`. */
  operation?: string;
  /**
   * The sentence of a rule error of the project (`extensions.userMessage`),
   * with the limits applied. Only with `errorClass` `rule`.
   */
  userMessage?: UserMessage;
  /** The value that was thrown, untouched. */
  cause: unknown;
}>;

type NormalizeErrorOptions = Readonly<{
  /** Operation name for errors that do not carry one themselves. */
  operation?: string;
}>;

const normalizedErrors = new WeakSet<object>();

export function isNormalizedError(value: unknown): value is NormalizedError {
  return isRecord(value) && normalizedErrors.has(value);
}

/**
 * An `ApolloError` keeps the server's errors in `graphQLErrors[]`, where the
 * error parsers do not look. The first one describes the failure.
 */
function getPrimaryGraphQLError(error: unknown): unknown {
  if (!isRecord(error) || !Array.isArray(error.graphQLErrors)) {
    return undefined;
  }
  return error.graphQLErrors[0];
}

function getWireString(
  error: unknown,
  target: unknown,
  field: "errorClass" | "errorCode" | "errorRef",
): string | undefined {
  if (error instanceof GraphQLRequestError) {
    return field === "errorCode" ? error.debugInfos?.errorCode : error[field];
  }
  const value =
    getStringAtPath(target, ["extensions", field]) ??
    getStringAtPath(target, [field]);
  return value || undefined;
}

/**
 * An error the backend classified carries its code in `extensions.errorCode`
 * or has none; only an unclassified error is searched for a code in every
 * shape older backends used, its message included.
 */
function getCode(
  error: unknown,
  target: unknown,
  errorClass: string | undefined,
): string | undefined {
  return errorClass
    ? getWireString(error, target, "errorCode")
    : (getErrorCodeFromUnknown(target) ?? getErrorCodeFromUnknown(error));
}

function getUserMessage(
  error: unknown,
  target: unknown,
): UserMessage | undefined {
  return error instanceof GraphQLRequestError
    ? error.userMessage
    : getWireUserMessage(target);
}

/**
 * Reads code, type, class, reference, Sentry event id and operation from
 * whatever a transport threw. It never replaces the error: `cause` is the
 * value it was given. A value that is already normalised is returned as is.
 */
export function normalizeError(
  error: unknown,
  options?: NormalizeErrorOptions,
): NormalizedError {
  if (isNormalizedError(error)) {
    return error;
  }

  const target = getPrimaryGraphQLError(error) ?? error;
  const errorClass = getWireString(error, target, "errorClass");
  const normalized: NormalizedError = {
    code: getCode(error, target, errorClass),
    type: getErrorTypeFromUnknown(target) ?? getErrorTypeFromUnknown(error),
    errorClass,
    errorRef: getWireString(error, target, "errorRef"),
    sentryEventId: getSentryEventIdFromUnknown(error),
    operation:
      error instanceof GraphQLRequestError
        ? error.operationLabel
        : options?.operation,
    userMessage: getUserMessage(error, target),
    cause: error,
  };
  normalizedErrors.add(normalized);
  return normalized;
}
